import {
  ArrowLeftIcon,
  ArrowPathIcon,
  BoltIcon,
  CloudIcon,
  FireIcon,
  MagnifyingGlassIcon,
  MapPinIcon,
  Squares2X2Icon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { AnimatePresence, motion } from "framer-motion";
import { useRouter } from "next/router";
import { useEffect, useMemo, useRef, useState } from "react";
import { ROUTES } from "~/content/Routes";
import { CITIES, findNearestCity } from "~/lib/cities";
import {
  SERVICES_BY_CATEGORY,
  getCityServicePath,
  getServiceBySlug,
  type Service,
} from "~/lib/services";

type UiServiceCategory = {
  id: string;
  label: string;
  services: Service[];
};

const EMPTY_SERVICES: Service[] = [];

const CATEGORY_ICON_MAP = {
  Elektryczne: BoltIcon,
  Gazowe: FireIcon,
  "Kominiarskie i wentylacyjne": CloudIcon,
  Inne: Squares2X2Icon,
} as const;

const SERVICE_CATEGORIES: UiServiceCategory[] = SERVICES_BY_CATEGORY.map(
  (category, index) => ({
    id: `service-category-${index}`,
    label: category.category,
    services: category.services,
  })
);

const CITY_OPTIONS = [...CITIES].sort((a, b) =>
  a.name.localeCompare(b.name, "pl")
);

const normalizeSearchTerm = (value: string): string =>
  value
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim();

type Props = {
  initialServiceSlug?: string;
  mode?: "discovery" | "service";
  className?: string;
};

const getCategoryByServiceSlug = (
  serviceSlug?: string
): UiServiceCategory | null => {
  if (!serviceSlug) return null;
  const matchedCategory = SERVICES_BY_CATEGORY.find((category) =>
    category.services.some((service) => service.slug === serviceSlug)
  );
  if (!matchedCategory) return null;
  return (
    SERVICE_CATEGORIES.find(
      (category) => category.label === matchedCategory.category
    ) ?? null
  );
};

const getLocationPermissionErrorMessage = (
  error: GeolocationPositionError
): string => {
  switch (error.code) {
    case error.PERMISSION_DENIED:
      return "Aby podpowiedzieć miasto, potrzebujemy zgody na użycie lokalizacji.";
    case error.POSITION_UNAVAILABLE:
      return "Nie udało się odczytać Twojej lokalizacji. Spróbuj ponownie za chwilę.";
    case error.TIMEOUT:
      return "Pobieranie lokalizacji trwało zbyt długo. Spróbuj ponownie.";
    default:
      return "Nie udało się pobrać lokalizacji. Wybierz miasto ręcznie.";
  }
};

const cardVariants = {
  enter: (dir: number) => ({ x: dir > 0 ? 40 : -40, opacity: 0 }),
  center: {
    x: 0,
    opacity: 1,
    transition: { duration: 0.22, ease: "easeOut" as const },
  },
  exit: (dir: number) => ({
    x: dir > 0 ? -40 : 40,
    opacity: 0,
    transition: { duration: 0.18, ease: "easeIn" as const },
  }),
};

const ServiceSelectorInner: React.FC<Props> = ({
  initialServiceSlug,
  mode,
  className,
}) => {
  const router = useRouter();

  const initialCategory = useMemo(
    () => getCategoryByServiceSlug(initialServiceSlug),
    [initialServiceSlug]
  );
  const initialService = useMemo(
    () =>
      initialServiceSlug ? getServiceBySlug(initialServiceSlug) ?? null : null,
    [initialServiceSlug]
  );
  const selectorMode = mode ?? (initialService ? "service" : "discovery");
  const isServiceMode = selectorMode === "service" && Boolean(initialService);

  const containerRef = useRef<HTMLDivElement>(null);

  const [cityInput, setCityInput] = useState("");
  const [selectedCitySlug, setSelectedCitySlug] = useState<string | null>(null);
  const [cityDropdownOpen, setCityDropdownOpen] = useState(false);

  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(
    initialCategory?.id ?? null
  );

  const [selectedServiceSlug, setSelectedServiceSlug] = useState<string | null>(
    initialService?.slug ?? null
  );

  const [isDetectingCity, setIsDetectingCity] = useState(false);
  const [locationError, setLocationError] = useState<string | null>(null);

  const [step, setStep] = useState<0 | 1 | 2>(
    initialCategory ? (initialService ? 2 : 1) : 0
  );
  const [direction, setDirection] = useState(1);

  const selectedCategory = useMemo(
    () => SERVICE_CATEGORIES.find((cat) => cat.id === selectedCategoryId),
    [selectedCategoryId]
  );
  const selectedCategoryIcon = selectedCategory
    ? CATEGORY_ICON_MAP[selectedCategory.label as keyof typeof CATEGORY_ICON_MAP]
    : null;
  const SelectedCategoryIcon = selectedCategoryIcon;
  const serviceOptions = selectedCategory?.services ?? EMPTY_SERVICES;
  const selectedService = useMemo(
    () => serviceOptions.find((s) => s.slug === selectedServiceSlug) ?? null,
    [selectedServiceSlug, serviceOptions]
  );
  const canSubmit = Boolean(
    selectedCategory &&
      selectedService &&
      (selectedService.cityPages ? selectedCitySlug : true)
  );
  const canUseCurrentLocation =
    typeof navigator !== "undefined" && "geolocation" in navigator;

  useEffect(() => {
    const onOutsideClick = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setCityDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", onOutsideClick);
    return () => document.removeEventListener("mousedown", onOutsideClick);
  }, []);

  const filteredCities = useMemo(() => {
    const normalizedInput = normalizeSearchTerm(cityInput);
    if (!normalizedInput) return CITY_OPTIONS;
    return CITY_OPTIONS.filter((city) =>
      normalizeSearchTerm(`${city.name} ${city.slug}`).includes(normalizedInput)
    );
  }, [cityInput]);

  const goTo = (nextStep: 0 | 1 | 2, dir: 1 | -1) => {
    setDirection(dir);
    setStep(nextStep);
  };

  const selectCityOption = (slug: string, name: string) => {
    setCityInput(name);
    setSelectedCitySlug(slug);
    setLocationError(null);
    setCityDropdownOpen(false);
    if (!isServiceMode) {
      goTo(1, 1);
    } else if (initialService?.cityPages) {
      void router.push(getCityServicePath(slug, initialService.slug));
    }
  };

  const selectCategoryOption = (category: UiServiceCategory) => {
    setSelectedCategoryId(category.id);
    setSelectedServiceSlug(null);
    setLocationError(null);
  };

  const resetCity = () => {
    setCityInput("");
    setSelectedCitySlug(null);
    setLocationError(null);
    if (!isServiceMode) {
      goTo(0, -1);
    }
  };

  const resetCategory = () => {
    setSelectedCategoryId(null);
    setSelectedServiceSlug(null);
    setLocationError(null);
    goTo(1, -1);
  };

  const handleUseCurrentLocation = () => {
    if (isDetectingCity) return;
    if (typeof window !== "undefined" && !window.isSecureContext) {
      setLocationError("Lokalizacja działa tylko na HTTPS lub localhost.");
      return;
    }
    if (!("geolocation" in navigator)) {
      setLocationError("Ta przeglądarka nie wspiera pobierania lokalizacji.");
      return;
    }
    setIsDetectingCity(true);
    setLocationError(null);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const matchedCity = findNearestCity(
          position.coords.latitude,
          position.coords.longitude
        );
        if (!matchedCity) {
          setLocationError(
            "Nie udało się dopasować najbliższego miasta. Wybierz je ręcznie z listy."
          );
          setIsDetectingCity(false);
          return;
        }
        selectCityOption(matchedCity.slug, matchedCity.name);
        setIsDetectingCity(false);
      },
      (error) => {
        setLocationError(getLocationPermissionErrorMessage(error));
        setIsDetectingCity(false);
      },
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 }
    );
  };

  const handleNavigate = () => {
    if (!selectedService) return;
    if (selectedService.cityPages) {
      if (!selectedCitySlug) return;
      void router.push(getCityServicePath(selectedCitySlug, selectedService.slug));
      return;
    }
    void router.push(selectedService.href);
  };

  const renderCityInput = () => (
    <>
      <div className="relative">
        <div className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-slate-400">
          <MagnifyingGlassIcon className="h-4 w-4" />
        </div>
        <input
          type="text"
          value={cityInput}
          onFocus={() => setCityDropdownOpen(true)}
          onChange={(e) => {
            setCityInput(e.target.value);
            setSelectedCitySlug(null);
            setCityDropdownOpen(true);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" && filteredCities.length > 0) {
              e.preventDefault();
              const first = filteredCities[0];
              selectCityOption(first.slug, first.name);
            }
          }}
          placeholder="Np. Kraków"
          autoComplete="off"
          className="h-11 w-full rounded-xl border border-[#E5E7EB] bg-white pl-9 pr-9 text-sm text-slate-900 placeholder:text-slate-400 focus:border-sky-300 focus:outline-none focus:ring-2 focus:ring-sky-200/50"
        />
        {cityInput && (
          <button
            type="button"
            onClick={() => {
              setCityInput("");
              setSelectedCitySlug(null);
            }}
            className="absolute inset-y-0 right-3 flex items-center text-slate-400 transition-colors hover:text-slate-700"
          >
            <XMarkIcon className="h-4 w-4" />
          </button>
        )}
        {cityDropdownOpen && filteredCities.length > 0 && (
          <div className="absolute left-0 right-0 top-full z-50 mt-1 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl">
            <ul className="max-h-48 overflow-y-auto py-1">
              {filteredCities.map((city) => (
                <li key={city.slug}>
                  <button
                    type="button"
                    onClick={() => selectCityOption(city.slug, city.name)}
                    className="w-full px-3 py-2 text-left text-sm text-gray-700 transition-colors hover:bg-sky-50 hover:text-sky-900"
                  >
                    {city.name}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
      <div className="mt-4">
        <button
          type="button"
          onClick={handleUseCurrentLocation}
          disabled={!canUseCurrentLocation || isDetectingCity}
          className="inline-flex items-center gap-1.5 text-sm font-medium text-[#8DE3C9] transition-colors hover:text-white disabled:text-white/40"
        >
          {isDetectingCity ? (
            <ArrowPathIcon className="h-5 w-5 animate-spin" />
          ) : (
            <MapPinIcon className="h-5 w-5" />
          )}
          Użyj mojej lokalizacji
        </button>
      </div>
      {locationError && (
        <p className="mt-2 text-xs text-[#FFD3C4]">{locationError}</p>
      )}
    </>
  );

  return (
    <section
      id="service-selector"
      className={`relative mx-auto flex h-[340px] w-full max-w-lg scroll-mt-24 flex-col overflow-visible rounded-3xl bg-gradient-to-br from-[#071526] via-[#0C1F3F] to-[#163259] px-5 py-6 text-white shadow-[0_30px_80px_-40px_rgba(12,31,63,0.5)]${className ? ` ${className}` : ""}`}
    >
      {/* Decorative blobs – clipped inside card */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden rounded-3xl">
        <div className="absolute -right-20 -top-20 h-56 w-56 rounded-full bg-[#00C896]/18 blur-3xl" />
        <div className="absolute -left-16 bottom-0 h-48 w-48 rounded-full bg-[#8DE3C9]/18 blur-3xl" />
      </div>

      {/* Static header */}
      {(isServiceMode || step === 0) && (
        <div className="relative z-10">
          <p className="text-lg font-semibold leading-tight">
            Przeglądy i inspekcje bezpieczeństwa
          </p>
          <p className="mt-3 text-sm text-white/70">
            Elektryka, gaz, wentylacja i ppoż w jednym miejscu.
          </p>
        </div>
      )}

      {isServiceMode && initialService && (
        <>
          <div className="relative z-10 mt-4 rounded-2xl bg-[#F7F8FA] p-4 text-[#0C1F3F]">
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#5C6370]">
              Wybrana usługa
            </p>
            <div className="mt-2 flex items-start gap-3">
              {SelectedCategoryIcon ? (
                <SelectedCategoryIcon className="mt-0.5 h-5 w-5 shrink-0 text-[#00A87A]" />
              ) : null}
              <div>
                <p className="text-sm font-semibold text-[#0C1F3F]">
                  {initialService.name}
                </p>
                <p className="mt-1 text-xs leading-relaxed text-[#5C6370]">
                  {initialService.description}
                </p>
              </div>
            </div>
          </div>

          <div ref={containerRef} className="relative z-10 mt-4">
            {initialService.cityPages ? (
              <div>
                <p className="mb-2.5 text-sm font-medium text-white/90">
                  W jakim mieście szukasz tej usługi?
                </p>
                {renderCityInput()}
              </div>
            ) : (
              <div className="rounded-2xl bg-[#F7F8FA] p-4 text-[#0C1F3F]">
                <p className="text-sm font-semibold text-[#0C1F3F]">
                  Ta usługa nie wymaga wyboru miasta.
                </p>
                <p className="mt-1 text-xs leading-relaxed text-[#5C6370]">
                  Na tej stronie lepszym kolejnym krokiem jest bezpośredni
                  kontakt albo przejście do pełnego selektora, jeśli chcesz
                  sprawdzić inną usługę.
                </p>
                <div className="mt-4 grid gap-2 sm:grid-cols-2">
                  <button
                    type="button"
                    onClick={() => void router.push(ROUTES.CONTACT_US)}
                    className="h-11 rounded-xl bg-[#FF6B35] text-sm font-semibold text-white transition-colors hover:bg-[#E85A25]"
                  >
                    Skontaktuj się
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      void router.push(`${ROUTES.BOOK_NOW}#service-selector`)
                    }
                    className="h-11 rounded-xl border border-[#E5E7EB] bg-white text-sm font-semibold text-[#0C1F3F] transition-colors hover:bg-[#F7F8FA]"
                  >
                    Wybierz inną usługę
                  </button>
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {/* Animated step cards */}
      {!isServiceMode && (
        <div ref={containerRef} className="relative z-10 mt-3 flex-1">
          <AnimatePresence custom={direction} mode="wait">
            <motion.div
              key={step}
              custom={direction}
              variants={cardVariants}
              initial="enter"
              animate="center"
              exit="exit"
            >
              {/* ── Step 0: City ── */}
              {step === 0 && (
                <div className="mt-6">
                  <p className="mb-2.5 text-sm font-medium text-white/90">
                    Gdzie szukasz?
                  </p>
                  {renderCityInput()}
                </div>
              )}

              {/* ── Step 1: Category ── */}
              {step === 1 && (
                <div>
                  <p className="mb-1.5 text-sm font-medium text-white/90">
                    Co chcesz sprawdzić?
                  </p>
                  <ul className="space-y-1">
                    {SERVICE_CATEGORIES.map((category) => {
                      const Icon =
                        CATEGORY_ICON_MAP[
                          category.label as keyof typeof CATEGORY_ICON_MAP
                        ];
                      const isSelected = selectedCategoryId === category.id;
                      return (
                        <li key={category.id}>
                          <button
                            type="button"
                            onClick={() => selectCategoryOption(category)}
                            className={`flex w-full items-center gap-3 rounded-xl px-3 py-2 text-sm font-medium transition-colors ${
                              isSelected
                                ? "bg-[#E8FAF4] text-[#0C1F3F]"
                                : "bg-white text-[#0C1F3F] hover:bg-[#F7F8FA]"
                            }`}
                          >
                            {Icon && <Icon className="h-4 w-4 shrink-0" />}
                            {category.label}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}

              {/* ── Step 2: Service ── */}
              {step === 2 && (
                <div>
                  <p className="mb-1.5 text-sm font-medium text-white/90">
                    Wybierz usługę
                  </p>
                  <ul className="max-h-[170px] space-y-1 overflow-y-auto">
                    {serviceOptions.map((service) => {
                      const isSelected = selectedServiceSlug === service.slug;
                      return (
                        <li key={service.slug}>
                          <button
                            type="button"
                            onClick={() =>
                              setSelectedServiceSlug(service.slug)
                            }
                            className={`flex w-full items-center rounded-xl px-3 py-2 text-sm font-medium transition-colors ${
                              isSelected
                                ? "bg-[#E8FAF4] text-[#0C1F3F]"
                                : "bg-white text-[#0C1F3F] hover:bg-[#F7F8FA]"
                            }`}
                          >
                            {service.name}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
            </motion.div>
          </AnimatePresence>
        </div>
      )}

      {/* Bottom navigation */}
      {!isServiceMode && step > 0 && (
        <div className="relative z-10 mt-3 flex gap-2">
          <button
            type="button"
            onClick={() => (step === 2 ? resetCategory() : resetCity())}
            className="flex h-10 flex-1 items-center justify-center gap-1.5 rounded-xl border border-[#E5E7EB] bg-white text-sm font-medium text-[#0C1F3F] transition-colors hover:bg-[#F7F8FA]"
          >
            <ArrowLeftIcon className="h-3.5 w-3.5" />
            Cofnij
          </button>
          {step === 1 && (
            <button
              type="button"
              disabled={!selectedCategoryId}
              onClick={() => goTo(2, 1)}
              className="h-10 flex-1 rounded-xl bg-[#FF6B35] text-sm font-semibold text-white transition-colors hover:bg-[#E85A25] disabled:cursor-not-allowed disabled:opacity-40"
            >
              Dalej
            </button>
          )}
          {step === 2 && (
            <button
              type="button"
              disabled={!canSubmit}
              onClick={handleNavigate}
              className="h-10 flex-1 rounded-xl bg-[#FF6B35] text-sm font-semibold text-white transition-colors hover:bg-[#E85A25] disabled:cursor-not-allowed disabled:opacity-40"
            >
              Szukaj
            </button>
          )}
        </div>
      )}

      {/* Step indicator dots – appear progressively */}
      {!isServiceMode && (
        <div className="relative z-10 mt-3 flex items-center justify-center gap-1.5">
          <AnimatePresence>
            {([0, 1, 2] as const).filter((i) => i <= step).map((i) => (
              <motion.div
                key={i}
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.25, ease: "easeOut" }}
                className="h-1.5 w-1.5 rounded-full bg-white"
              />
            ))}
          </AnimatePresence>
        </div>
      )}
    </section>
  );
};

const ServiceSelector: React.FC<Props> = ({ initialServiceSlug, mode, className }) => (
  <ServiceSelectorInner
    key={`${mode ?? "auto"}-${initialServiceSlug ?? "service-selector-default"}`}
    initialServiceSlug={initialServiceSlug}
    mode={mode}
    className={className}
  />
);

export default ServiceSelector;
